import { BreadcrumbBuffer } from './breadcrumbs'
import { computeFingerprint } from './fingerprint'
import { ReportLimiter } from './limiter'
import { sendReport } from './transport'
import type {
  Breadcrumb,
  ErrorReportPayload,
  ErrorReportType,
  ErrorReporter,
  ErrorReporterConfig,
  ReportContext,
} from './types'

const MAX_NAME_CHARS = 200
const MAX_MESSAGE_CHARS = 1_000
const MAX_STACK_CHARS = 16 * 1_024
const MAX_URL_CHARS = 1_000
const MAX_CONTEXT_JSON_CHARS = 8 * 1_024
const MAX_PRE_AUTH_QUEUE = 5

const DEFAULT_MAX_PER_SESSION = 25
const DEFAULT_MAX_PER_FINGERPRINT = 3
const DEFAULT_MIN_INTERVAL_MS = 2_000
const SEND_BURST = 5

function randomSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const bytes = new Uint8Array(8)
      crypto.getRandomValues(bytes)
      return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    }
  } catch {
    // fall through
  }
  return Math.random().toString(16).slice(2, 10) + Math.random().toString(16).slice(2, 10)
}

interface NormalizedError {
  name: string
  message: string
  stack?: string
}

function normalizeError(error: unknown): NormalizedError {
  if (error instanceof Error) {
    return {
      name: String(error.name || 'Error').slice(0, MAX_NAME_CHARS),
      message: String(error.message ?? '').slice(0, MAX_MESSAGE_CHARS),
      stack: typeof error.stack === 'string' ? error.stack.slice(0, MAX_STACK_CHARS) : undefined,
    }
  }
  let message: string
  try {
    message = typeof error === 'string' ? error : JSON.stringify(error) ?? String(error)
  } catch {
    message = String(error)
  }
  return { name: 'NonError', message: String(message).slice(0, MAX_MESSAGE_CHARS) }
}

/** context 를 직렬화 가능·크기 제한 내로 보장한다 (순환 참조·과대 객체 방어) */
function sanitizeContext(context: Record<string, unknown>): Record<string, unknown> | undefined {
  try {
    const json = JSON.stringify(context)
    if (json === undefined || json === '{}') return undefined
    if (json.length > MAX_CONTEXT_JSON_CHARS) return { truncated: true }
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    return { unserializable: true }
  }
}

export function createErrorReporter(config: ErrorReporterConfig): ErrorReporter {
  const sessionId = randomSessionId()
  const breadcrumbs = new BreadcrumbBuffer()
  const limiter = new ReportLimiter({
    maxPerSession: config.maxReportsPerSession ?? DEFAULT_MAX_PER_SESSION,
    maxPerFingerprint: config.maxPerFingerprint ?? DEFAULT_MAX_PER_FINGERPRINT,
    minIntervalMs: config.minSendIntervalMs ?? DEFAULT_MIN_INTERVAL_MS,
    burst: SEND_BURST,
  })
  const fetchFn = config.fetchFn ?? (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : undefined)
  const preAuthQueue: ErrorReportPayload[] = []
  let installed = false

  // 재진입 가드: 캡처 처리 중 발생한 모든 에러/크럼은 무시한다.
  // 리포터 내부 결함이 어떤 경로로도 증폭되지 않게 하는 1차 방어선.
  let inCapture = false

  function isEnabledSafe(): boolean {
    try {
      return config.isEnabled()
    } catch {
      return false
    }
  }

  function buildPayload(
    normalized: NormalizedError,
    errorType: ErrorReportType,
    context?: Record<string, unknown>,
  ): ErrorReportPayload {
    let route: string | undefined
    try {
      route = config.getRoute?.()
    } catch {
      route = undefined
    }
    return {
      ...normalized,
      errorType,
      fingerprint: computeFingerprint(normalized.name, normalized.message, normalized.stack),
      sessionId,
      url: typeof location !== 'undefined' ? location.href.slice(0, MAX_URL_CHARS) : '',
      route,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      language: typeof navigator !== 'undefined' ? navigator.language : '',
      viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
      buildId: config.buildId,
      breadcrumbs: breadcrumbs.snapshot(),
      context: context ? sanitizeContext(context) : undefined,
      occurredAt: new Date().toISOString(),
    }
  }

  function dispatch(payload: ErrorReportPayload): void {
    if (!fetchFn) return
    let final: ErrorReportPayload | null = payload
    if (config.beforeSend) {
      try {
        final = config.beforeSend(payload)
      } catch {
        final = null
      }
    }
    if (final) {
      sendReport(config.endpoint, final, fetchFn)
    }
  }

  function capture(error: unknown, errorType: ErrorReportType, context?: Record<string, unknown>): void {
    if (inCapture) return
    inCapture = true
    try {
      const normalized = normalizeError(error)
      const payload = buildPayload(normalized, errorType, context)
      if (!limiter.allow(payload.fingerprint, Date.now())) return

      if (!isEnabledSafe()) {
        if (preAuthQueue.length < MAX_PRE_AUTH_QUEUE) {
          preAuthQueue.push(payload)
        }
        return
      }
      dispatch(payload)
    } catch {
      // 캡처 자체의 실패는 무시
    } finally {
      inCapture = false
    }
  }

  function handleErrorEvent(event: ErrorEvent): void {
    if (event.error != null) {
      capture(event.error, 'error')
      return
    }
    if (!event.message) return
    // error 객체가 없는 경우 — 대부분 크로스오리진 'Script error.' 마스킹이다.
    // new Error(message) 를 그대로 캡처하면 이 핸들러(리포터 자신)의 호출 지점이
    // stack 으로 박혀 실제 발생 위치를 가리므로, 합성 stack 을 만들지 않는다.
    const synthetic = new Error(String(event.message))
    synthetic.name = 'ScriptError'
    // 브라우저가 위치를 노출한 경우(동일 출처지만 error 객체가 없는 일부 케이스)에만
    // 실제 파일·행·열을 stack 으로 보존한다. 마스킹된 불투명 이벤트는 위치가 없다.
    synthetic.stack = event.filename
      ? `${String(event.message)}\n    at ${event.filename}:${String(event.lineno)}:${String(event.colno)}`
      : undefined
    // filename 조차 없으면 출처가 가려진 불투명 이벤트 — 코드 버그가 아니라
    // 환경(주로 인앱 웹뷰·번역기 주입 스크립트) 신호다. context.opaque 로 표식한다.
    capture(synthetic, 'error', event.filename ? undefined : { opaque: true })
  }

  function handleRejection(event: PromiseRejectionEvent): void {
    capture(event.reason, 'unhandledrejection')
  }

  return {
    sessionId,

    install() {
      if (installed || typeof window === 'undefined') return
      window.addEventListener('error', handleErrorEvent)
      window.addEventListener('unhandledrejection', handleRejection)
      installed = true
    },

    uninstall() {
      if (!installed || typeof window === 'undefined') return
      window.removeEventListener('error', handleErrorEvent)
      window.removeEventListener('unhandledrejection', handleRejection)
      installed = false
    },

    reportError(error: unknown, context?: ReportContext) {
      const { errorType, ...rest } = context ?? {}
      capture(error, errorType ?? 'manual', Object.keys(rest).length > 0 ? rest : undefined)
    },

    addBreadcrumb(crumb: Omit<Breadcrumb, 'ts'>) {
      if (inCapture) return
      try {
        breadcrumbs.push(crumb, Date.now())
      } catch {
        // 무시
      }
    },

    flush() {
      if (inCapture || preAuthQueue.length === 0 || !isEnabledSafe()) return
      inCapture = true
      try {
        for (const payload of preAuthQueue.splice(0)) {
          dispatch(payload)
        }
      } catch {
        // 무시
      } finally {
        inCapture = false
      }
    },
  }
}
